import { groupLabel } from "./access-group-labels";
import { Link } from "react-router";
import { ArrowUpRight } from "lucide-react";
import { buttonClasses } from "@/components/design/button-classes";
import { useRef, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { useIntl } from "react-intl";
import { Disclosure } from "@/components/design/Disclosure";
import { Button } from "@/components/design/Button";
import { PersonResultRow } from "@/components/common/PersonResultRow";
import { AccessRoleMembersDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

export function AccessRoleAssignmentMembers({ id, roleId, groupId, groupName, scopeName, personCount }: {
  id?: string;
  roleId: string;
  groupId: string;
  groupName: string;
  scopeName: string;
  personCount: number;
}) {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, string | number>) => intl.formatMessage({ id }, values);
  const [open, setOpen] = useState(false);
  const [requested, setRequested] = useState(false);
  const [pageError, setPageError] = useState<string | null>(null);
  const inFlight = useRef(false);
  const { data, loading, error, fetchMore, refetch } = useQuery(AccessRoleMembersDocument, {
    variables: { roleId, groupId, after: null }, skip: !requested,
    fetchPolicy: "network-only", notifyOnNetworkStatusChange: true,
  });
  const result = data?.accessRoleMembers;

  async function loadMore() {
    if (loading || inFlight.current || !result?.hasNextPage || !result.nextAfter) return;
    inFlight.current = true;
    setPageError(null);
    try {
      await fetchMore({ variables: { after: result.nextAfter }, updateQuery(previous, { fetchMoreResult }) {
        const page = fetchMoreResult.accessRoleMembers;
        const members = new Map(previous.accessRoleMembers.members.map((member) => [member.id, member]));
        page.members.forEach((member) => members.set(member.id, member));
        return { ...previous, accessRoleMembers: { ...page, members: [...members.values()] } };
      } });
    } catch (cause) { setPageError(userErrorMessage(intl, cause)); }
    finally { inFlight.current = false; }
  }

  return <Disclosure id={id} title={<span>
    <span className="block break-words">{groupLabel(intl, { id: groupId, name: groupName })}</span>
    <span className="mt-1 block text-smycles-meta font-normal text-ink-muted">{t("admin.roles.scope", { name: scopeName })}</span>
  </span>} count={t("admin.roles.peopleCount", { count: personCount })} open={open}
    onToggle={() => { setOpen((value) => !value); setRequested(true); }}>
    <div className="mb-4 flex justify-end">
      <Link id={id ? `${id}-manage` : undefined}
        className={buttonClasses({ variant: "secondary", size: "compact", className: "gap-2" })}
        aria-label={t("admin.roles.manageGroup", { name: groupLabel(intl, { id: groupId, name: groupName }) })}
        to={`/admin/settings?section=accessGroups&groupId=${encodeURIComponent(groupId)}&groupAction=roles`}>
        {t("admin.roles.manageAssignment")}<ArrowUpRight size={14} aria-hidden="true" />
      </Link>
    </div>
    <ul aria-label={t("admin.roles.membersIn", { name: groupLabel(intl, { id: groupId, name: groupName }) })} className="divide-y divide-ink/10">
      {result?.members.map((member) => <li key={member.id}>
        <PersonResultRow embedded person={{ id: member.id, name: member.name || t("admin.roles.unnamedPerson"),
          avatarContent: member.person?.avatar?.content, avatarMime: member.person?.avatar?.mimeType }}
          title={member.name || t("admin.roles.unnamedPerson")} />
      </li>)}
    </ul>
    {loading && <p role="status" className="py-3 text-smycles-meta text-ink-muted">{t("admin.roles.membersLoading")}</p>}
    {(error || pageError) && <div className="space-y-2 py-3">
      <p role="alert" className="text-smycles-meta text-ink">{pageError ?? userErrorMessage(intl, error)}</p>
      <Button size="compact" variant="secondary" disabled={loading} onClick={() => {
        if (pageError) void loadMore();
        else void refetch().catch(() => undefined);
      }}>{t("admin.roles.retry")}</Button>
    </div>}
    {!loading && !error && result?.members.length === 0 && <p className="text-smycles-meta text-ink-muted">{t("admin.roles.noMembers")}</p>}
    {result?.hasNextPage && !pageError && <Button size="compact" variant="secondary" className="mt-4" disabled={loading}
      onClick={() => void loadMore()}>{t("common.loadMore")}</Button>}
  </Disclosure>;
}
